/**
 * Stage 73.20r — KYC / Data Confirmation: Admin Cycles page.
 *
 * Admin triggers a new cycle (scope + frequency) → backend generates
 * a confirmation row per in-scope employee. This page shows the
 * cycle list with progress, plus a status report drill-down.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Loader2, Plus, X, Download, AlertTriangle, CheckCircle2, ShieldQuestion, ListFilter, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useActiveCompany } from '../hooks/useActiveCompany';

import EmployeeAutocomplete from '../components/EmployeeAutocomplete';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SCOPE_LABEL = {
  all: 'All Employees',
  employees: 'Selected Employees',
  department: 'Department-wise',
  location: 'Location-wise',
  payroll_group: 'Payroll Group-wise',
  project: 'Project / Site-wise',
};
const FREQ_LABEL = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half-yearly',
  yearly: 'Yearly',
  on_demand: 'On Demand',
};

export default function KYCCycles() {
  const [selectedCompany] = useActiveCompany();
  const [cycles, setCycles] = useState([]);
  const [meta, setMeta] = useState({ scope_types: [], frequencies: [] });
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [report, setReport] = useState(null);
  const [drillCycle, setDrillCycle] = useState(null);
  const [drillData, setDrillData] = useState(null);

  const [form, setForm] = useState({
    name: '',
    scope_type: 'all',
    scope_values: [],
    frequency: 'quarterly',
    due_date: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const loadAll = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const [c, m, r] = await Promise.all([
        axios.get(`${API}/kyc/cycles?company_id=${selectedCompany}`, { withCredentials: true }),
        axios.get(`${API}/kyc/_meta`, { withCredentials: true }),
        axios.get(`${API}/kyc/reports/status`, { withCredentials: true }),
      ]);
      setCycles(c.data || []);
      setMeta(m.data || {});
      setReport(r.data || null);
      // Load departments + locations for the scope dropdown.
      try {
        const d = await axios.get(`${API}/masters/departments?company_id=${selectedCompany}`, { withCredentials: true });
        setDepartments((d.data || []).map(x => x.name || x.value || x));
      } catch (e) { /* fallback: empty */ }
      try {
        const l = await axios.get(`${API}/masters/work-locations?company_id=${selectedCompany}`, { withCredentials: true });
        setLocations((l.data || []).map(x => x.name || x.value || x));
      } catch (e) { /* fallback: empty */ }
    } catch (e) {
      toast.error('Load failed: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [selectedCompany]);

  useEffect(() => { loadAll(); }, [loadAll]);


  const submitCycle = async () => {
    if (!form.name.trim()) {
      toast.error('Name the cycle (e.g. "Q3 2026 KYC Round")');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/kyc/cycles`, {
        name: form.name.trim(),
        company_id: selectedCompany,
        scope_type: form.scope_type,
        scope_values: form.scope_type === 'all' ? [] : form.scope_values,
        frequency: form.frequency,
        due_date: form.due_date || null,
        notes: form.notes,
      }, { withCredentials: true });
      toast.success('KYC cycle created — confirmations dispatched to employees');
      setShowCreate(false);
      setForm({ name: '', scope_type: 'all', scope_values: [], frequency: 'quarterly', due_date: '', notes: '' });
      await loadAll();
    } catch (e) {
      toast.error('Failed: ' + (e?.response?.data?.detail || e.message));
    }
    setSubmitting(false);
  };

  const openDrill = async (cycle) => {
    setDrillCycle(cycle);
    setDrillData(null);
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API}/kyc/cycles/${cycle.id}`, { withCredentials: true }),
        axios.get(`${API}/kyc/reports/status?cycle_id=${cycle.id}`, { withCredentials: true }),
      ]);
      setDrillData({ ...c.data, report: r.data });
    } catch (e) {
      toast.error('Drill failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const downloadCSV = async (path, filename) => {
    try {
      const res = await axios.get(`${API}${path}`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const cancelCycle = async (cycle) => {
    if (!window.confirm(`Cancel "${cycle.name}"? Pending confirmations will be closed and no new corrections accepted.`)) return;
    try {
      await axios.post(`${API}/kyc/cycles/${cycle.id}/cancel`, {}, { withCredentials: true });
      toast.success('Cycle cancelled');
      await loadAll();
    } catch (e) {
      toast.error('Cancel failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // Scope-value picker:
  const scopeValuesUI = useMemo(() => {
    if (form.scope_type === 'all') return null;
    if (form.scope_type === 'employees') {
      return (
        <EmployeeAutocomplete
          multiple
          companyId={selectedCompany}
          selected={form.scope_values}
          onChange={(ids) => setForm(f => ({ ...f, scope_values: ids }))}
          data-testid="kyc-scope-employee-picker"
        />
      );
    }
    let options = [];
    if (form.scope_type === 'department') options = departments;
    else if (form.scope_type === 'location') options = locations;
    if (form.scope_type === 'payroll_group' || form.scope_type === 'project') {
      return (
        <textarea
          rows={2}
          placeholder={form.scope_type === 'payroll_group' ? 'Comma-separated payroll group IDs' : 'Comma-separated project/site IDs'}
          value={(form.scope_values || []).join(',')}
          onChange={e => setForm(f => ({ ...f, scope_values: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
          className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
          data-testid="kyc-scope-values-text"
        />
      );
    }
    return (
      <select
        multiple
        size={Math.min(6, Math.max(3, options.length))}
        value={form.scope_values}
        onChange={e => setForm(f => ({ ...f, scope_values: Array.from(e.target.selectedOptions).map(o => o.value) }))}
        className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
        data-testid="kyc-scope-values-select"
      >
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );
  }, [form.scope_type, form.scope_values, departments, locations, selectedCompany]);

  return (
    <MainLayout>
      <div className="space-y-6 p-6" data-testid="kyc-cycles-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ShieldQuestion className="w-6 h-6 text-blue-600" />
              KYC / Data Confirmation
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Trigger periodic employee-wise master-data confirmation rounds. Employees confirm or request corrections — HR approves.
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/payroll/kyc-corrections" data-testid="kyc-corrections-link">
              <Button variant="outline" size="sm"><ListFilter className="w-3 h-3 mr-1" /> Corrections Queue</Button>
            </Link>
            <Button variant="outline" size="sm" onClick={loadAll} disabled={loading}>
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            </Button>
            <Button size="sm" onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="kyc-new-cycle-btn">
              <Plus className="w-3 h-3 mr-1" /> Trigger Cycle
            </Button>
          </div>
        </div>

        {/* Tenant-wide overview */}
        {report && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatTile label="Total Confirmations" value={report.overall.total} color="slate" />
            <StatTile label="Pending" value={report.overall.pending} color="amber" />
            <StatTile label="Confirmed" value={report.overall.confirmed} color="emerald" />
            <StatTile label="Correction Requested" value={report.overall.correction_requested} color="rose" />
          </div>
        )}

        {/* Cycles list */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700">Cycles ({cycles.length})</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => downloadCSV('/kyc/reports/pending.csv', 'kyc_pending.csv')}>
                <Download className="w-3 h-3 mr-1" /> Pending CSV
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadCSV('/kyc/reports/corrections.csv', 'kyc_corrections.csv')}>
                <Download className="w-3 h-3 mr-1" /> Corrections CSV
              </Button>
            </div>
          </div>
          {cycles.length === 0 ? (
            <p className="text-xs text-slate-500 italic text-center py-8">No KYC cycles yet. Click "Trigger Cycle" to start the first one.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Scope</th>
                    <th className="text-left p-2">Frequency</th>
                    <th className="text-left p-2">Due</th>
                    <th className="text-right p-2">Progress</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Triggered</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody data-testid="kyc-cycles-table">
                  {cycles.map(c => {
                    const total = c.stats?.total || 0;
                    const done = (c.stats?.confirmed || 0) + (c.stats?.correction_requested || 0);
                    const pct = total ? Math.round((done / total) * 100) : 0;
                    return (
                      <tr key={c.id} className="border-t border-slate-100 hover:bg-blue-50/40" data-testid={`kyc-cycle-row-${c.id}`}>
                        <td className="p-2">
                          <button onClick={() => openDrill(c)} className="text-left hover:underline" data-testid={`kyc-cycle-drill-${c.id}`}>
                            <div className="font-medium text-slate-800">{c.name}</div>
                            {c.notes && <div className="text-[10px] text-slate-500">{c.notes}</div>}
                          </button>
                        </td>
                        <td className="p-2 text-slate-600">{SCOPE_LABEL[c.scope_type]}</td>
                        <td className="p-2 text-slate-600">{FREQ_LABEL[c.frequency]}</td>
                        <td className="p-2">
                          <span className={c.is_overdue ? 'text-rose-600 font-semibold' : 'text-slate-600'}>{c.due_date}</span>
                          {c.is_overdue && <AlertTriangle className="w-3 h-3 inline ml-1 text-rose-600" />}
                        </td>
                        <td className="p-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <span className="font-mono text-[10px]">{done}/{total}</span>
                            <div className="w-20 h-1.5 bg-slate-200 rounded overflow-hidden">
                              <div className={`h-full ${pct === 100 ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        </td>
                        <td className="p-2">
                          <span className={`px-2 py-0.5 rounded text-[10px] ${c.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>{c.status}</span>
                        </td>
                        <td className="p-2 text-slate-500">
                          <div>{c.triggered_by_name}</div>
                          <div className="text-[10px]">{(c.triggered_at || '').slice(0, 10)}</div>
                        </td>
                        <td className="p-2 text-right">
                          {c.status === 'active' && (
                            <button onClick={() => cancelCycle(c)} className="text-rose-500 hover:text-rose-700 text-[10px] font-medium">Cancel</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* CREATE MODAL */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()} data-testid="kyc-create-modal">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Trigger KYC Cycle</h3>
                <button onClick={() => setShowCreate(false)}><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Cycle Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Q3 2026 KYC Round"
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                    data-testid="kyc-form-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Scope *</label>
                    <select
                      value={form.scope_type}
                      onChange={e => setForm({ ...form, scope_type: e.target.value, scope_values: [] })}
                      className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                      data-testid="kyc-form-scope"
                    >
                      {meta.scope_types.map(s => <option key={s} value={s}>{SCOPE_LABEL[s] || s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Frequency *</label>
                    <select
                      value={form.frequency}
                      onChange={e => setForm({ ...form, frequency: e.target.value })}
                      className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                      data-testid="kyc-form-freq"
                    >
                      {meta.frequencies.map(f => <option key={f} value={f}>{FREQ_LABEL[f] || f}</option>)}
                    </select>
                  </div>
                </div>
                {form.scope_type !== 'all' && (
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Scope Values</label>
                    {scopeValuesUI}
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Due Date (optional)</label>
                  <input
                    type="date"
                    value={form.due_date}
                    onChange={e => setForm({ ...form, due_date: e.target.value })}
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                  />
                  <p className="text-[10px] text-slate-500 mt-0.5">If empty: 14 days from today.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Notes</label>
                  <textarea
                    rows={2}
                    value={form.notes}
                    onChange={e => setForm({ ...form, notes: e.target.value })}
                    placeholder="e.g. Quarterly mandatory KYC for finance compliance"
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                  />
                </div>
                <Button onClick={submitCycle} disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="kyc-submit-cycle">
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Generate Confirmations'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* DRILL MODAL */}
        {drillCycle && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setDrillCycle(null)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[88vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold">{drillCycle.name}</h3>
                  <p className="text-[11px] text-slate-500">{SCOPE_LABEL[drillCycle.scope_type]} · {FREQ_LABEL[drillCycle.frequency]} · Due {drillCycle.due_date}</p>
                </div>
                <button onClick={() => setDrillCycle(null)}><X className="w-4 h-4" /></button>
              </div>
              {!drillData ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-2 text-center text-xs">
                    <div className="bg-slate-100 rounded p-2"><div className="font-bold text-slate-700">{drillData.report.overall.total}</div><div>Total</div></div>
                    <div className="bg-amber-100 rounded p-2"><div className="font-bold text-amber-700">{drillData.report.overall.pending}</div><div>Pending</div></div>
                    <div className="bg-emerald-100 rounded p-2"><div className="font-bold text-emerald-700">{drillData.report.overall.confirmed}</div><div>Confirmed</div></div>
                    <div className="bg-rose-100 rounded p-2"><div className="font-bold text-rose-700">{drillData.report.overall.correction_requested}</div><div>Correction Requested</div></div>
                  </div>
                  {drillData.report.by_department.length > 1 && (
                    <Card className="p-3">
                      <h4 className="text-xs font-semibold mb-2">By Department</h4>
                      <PivotTable rows={drillData.report.by_department} />
                    </Card>
                  )}
                  {drillData.report.by_location.length > 1 && (
                    <Card className="p-3">
                      <h4 className="text-xs font-semibold mb-2">By Location</h4>
                      <PivotTable rows={drillData.report.by_location} />
                    </Card>
                  )}
                  <Card className="p-3">
                    <h4 className="text-xs font-semibold mb-2">Employee Status ({drillData.confirmations.length})</h4>
                    <div className="max-h-72 overflow-y-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr><th className="text-left p-1">Code</th><th className="text-left p-1">Name</th><th className="text-left p-1">Dept</th><th className="text-left p-1">Location</th><th className="text-left p-1">Status</th></tr>
                        </thead>
                        <tbody>
                          {drillData.confirmations.map(c => (
                            <tr key={c.id} className="border-t">
                              <td className="p-1 font-mono">{c.employee_code}</td>
                              <td className="p-1">{c.employee_name}</td>
                              <td className="p-1">{c.department}</td>
                              <td className="p-1">{c.work_location}</td>
                              <td className="p-1">
                                {c.status === 'confirmed' && <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" /> Confirmed</span>}
                                {c.status === 'pending' && <span className="text-amber-700">Pending</span>}
                                {c.status === 'correction_requested' && <span className="text-rose-700">Correction</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}

function StatTile({ label, value, color }) {
  const map = {
    slate: 'bg-slate-100 text-slate-700',
    amber: 'bg-amber-100 text-amber-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    rose: 'bg-rose-100 text-rose-700',
  };
  return (
    <Card className={`p-3 ${map[color]}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </Card>
  );
}

function PivotTable({ rows }) {
  return (
    <table className="w-full text-[11px]">
      <thead className="bg-slate-50">
        <tr>
          <th className="text-left p-1">Name</th>
          <th className="text-right p-1">Total</th>
          <th className="text-right p-1">Pending</th>
          <th className="text-right p-1">Confirmed</th>
          <th className="text-right p-1">Correction</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.name} className="border-t">
            <td className="p-1">{r.name}</td>
            <td className="p-1 text-right font-mono">{r.total}</td>
            <td className="p-1 text-right text-amber-700">{r.pending}</td>
            <td className="p-1 text-right text-emerald-700">{r.confirmed}</td>
            <td className="p-1 text-right text-rose-700">{r.correction_requested}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Searchable multi-select employee picker. Stores ObjectIds in
 * `selected`, but the user types name/code and sees friendly chips.
 * Replaces the old comma-separated ObjectId textbox that confused
 * everyone in production (Stage 73.20r hotfix).
 */
function EmployeePicker({ companyId, selected, onChange }) {
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [selectedDetails, setSelectedDetails] = React.useState([]); // [{id, code, name}]
  const [searching, setSearching] = React.useState(false);
  const [showResults, setShowResults] = React.useState(false);

  // Pre-fetch labels for already-selected IDs (so chips show on re-open).
  React.useEffect(() => {
    const missing = selected.filter(id => !selectedDetails.find(d => d.id === id));
    if (missing.length === 0) return;
    (async () => {
      try {
        const fetched = await Promise.all(missing.map(id =>
          axios.get(`${API}/employees/${id}`, { withCredentials: true }).then(r => ({
            id,
            code: r.data?.employment_details?.employee_code || id.slice(0, 6),
            name: `${r.data?.personal_details?.first_name || ''} ${r.data?.personal_details?.last_name || ''}`.trim() || id.slice(0, 6),
          })).catch(() => ({ id, code: id.slice(0, 6), name: '(unknown)' })),
        ));
        setSelectedDetails(prev => [...prev, ...fetched]);
      } catch (e) { /* ignore */ }
    })();
  }, [selected, selectedDetails]);

  React.useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const qs = new URLSearchParams({ search: query.trim(), limit: '15' });
        if (companyId) qs.set('company_id', companyId);
        const res = await axios.get(`${API}/employees?${qs.toString()}`, { withCredentials: true });
        const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        setResults(items.slice(0, 15).map(e => ({
          id: e.id,
          code: e.employment_details?.employee_code || '',
          name: `${e.personal_details?.first_name || ''} ${e.personal_details?.last_name || ''}`.trim() || e.employment_details?.employee_code,
          department: e.employment_details?.department || '',
        })));
      } catch (e) { /* ignore */ }
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [query, companyId]);

  const add = (emp) => {
    if (selected.includes(emp.id)) return;
    onChange([...selected, emp.id]);
    setSelectedDetails(prev => [...prev.filter(d => d.id !== emp.id), emp]);
    setQuery('');
    setResults([]);
  };

  const remove = (id) => {
    onChange(selected.filter(x => x !== id));
    setSelectedDetails(prev => prev.filter(d => d.id !== id));
  };

  return (
    <div data-testid="kyc-employee-picker">
      <div className="flex flex-wrap gap-1 mb-1">
        {selectedDetails.map(d => (
          <span key={d.id} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-[11px] rounded px-2 py-0.5">
            <span className="font-mono">{d.code}</span>
            <span>·</span>
            <span>{d.name}</span>
            <button onClick={() => remove(d.id)} className="ml-1 hover:text-rose-600">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {selected.length === 0 && (
          <span className="text-[11px] italic text-slate-400">No employees picked yet — search below.</span>
        )}
      </div>
      <div className="relative">
        <div className="flex items-center border border-slate-300 rounded px-2 py-1.5">
          <Search className="w-3 h-3 text-slate-400 mr-1.5" />
          <input
            value={query}
            onChange={e => { setQuery(e.target.value); setShowResults(true); }}
            onFocus={() => setShowResults(true)}
            placeholder="Search by name or employee code (e.g. HSF001, Aarav)…"
            className="flex-1 outline-none text-sm bg-transparent"
            data-testid="kyc-emp-picker-search"
          />
          {searching && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
        </div>
        {showResults && results.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded shadow-lg max-h-56 overflow-y-auto" data-testid="kyc-emp-picker-results">
            {results.map(r => (
              <button
                key={r.id}
                type="button"
                onClick={() => add(r)}
                disabled={selected.includes(r.id)}
                className={`w-full text-left px-2 py-1.5 text-xs flex items-center justify-between hover:bg-blue-50 ${selected.includes(r.id) ? 'bg-slate-50 text-slate-400' : 'text-slate-700'}`}
                data-testid={`kyc-emp-picker-opt-${r.id}`}
              >
                <span>
                  <span className="font-mono text-[10px] mr-1 text-slate-500">{r.code}</span>
                  {r.name}
                  {r.department && <span className="text-[10px] text-slate-400 ml-1">· {r.department}</span>}
                </span>
                {selected.includes(r.id) && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="text-[10px] text-slate-500 mt-1">Tip: Employee codes (e.g. HSF001) also work — paste them comma-separated and click anywhere to apply.</p>
    </div>
  );
}
